import { createSelector } from "@reduxjs/toolkit";
import { OrderTypesEnum } from "../../../utils/enums";
import { inFranchiseDeliveryZoneV2 } from "../../../utils/help/delivery/delivery";
import type {
  DeliveryType,
  FranchiseDeliveryConfig,
} from "../../../utils/types";
import type { RootState } from "../app/store";

/**
 * The guest's delivery selection, derived rather than stored.
 *
 * The slice used to keep a COPY of the chosen `FranchiseDeliveryConfig` --
 * zones, polygons, fees and all -- and the fee/ETA/zone id resolved from it.
 * All of that is server data or a pure function of server data, and the whole
 * slice is persisted to localStorage, so a returning guest rehydrated whatever
 * was true the last time they used the site. `restaurantInfo` and
 * `selectedFranchise` self-heal from the page props on every load; the copy did
 * not, so an ops zone or fee edit stayed invisible to that guest indefinitely
 * -- even on a page core had correctly revalidated (prepits/project#4204).
 *
 * The fix is to stop keeping the copy. The slice now persists only what is
 * genuinely the guest's: which config they picked (`currentDeliveryConfigId`)
 * and which address they picked. Everything else is read back out of
 * `selectedFranchise.franchiseConfig.deliveryV2`, which is rebuilt from the
 * page on every load -- so staleness is not fixed here, it is unrepresentable.
 *
 * Memoised because these are read on every render of every subscriber, and
 * because `selectDeliveryZone` builds a new object: an unmemoised one would
 * hand `useSelector` a fresh reference each call and re-render forever.
 */

// Module-level so the "franchise carries no deliveryV2" case keeps handing back
// the same reference; a fresh `[]` per call would defeat the memoisation of
// every selector downstream of it.
const NO_DELIVERY_CONFIGS: FranchiseDeliveryConfig[] = [];

/**
 * Every delivery config this storefront can currently resolve.
 *
 * Not just the selected franchise's own list. The store never required the
 * chosen config to belong to the selected branch, and the flows rely on that:
 * the map screens re-resolve a branch when an address moves, so there is a
 * moment where the guest holds a config belonging to the branch that actually
 * covers them while another is still selected. Narrowing this to the selected
 * franchise would silently drop the selection in exactly that window.
 *
 * This is the lookup of last resort -- `selectCurrentDeliveryConfig` re-points
 * a foreign row onto the selected branch before handing it out, and only the
 * map's address-confirm flow reads it un-re-pointed (`selectStoredDeliveryConfig`).
 */
const selectDeliveryV2 = createSelector(
  [
    (state: RootState) => state.config?.selectedFranchise,
    (state: RootState) => state.config?.activeFranchises,
  ],
  (selectedFranchise, activeFranchises): FranchiseDeliveryConfig[] => [
    ...(selectedFranchise?.franchiseConfig?.deliveryV2 ?? []),
    ...(activeFranchises ?? []).flatMap(
      (franchise) => franchise?.franchiseConfig?.deliveryV2 ?? []
    ),
  ]
);

/** The selected branch's own delivery configs, and nothing else. */
const selectSelectedFranchiseDeliveryV2 = createSelector(
  [(state: RootState) => state.config?.selectedFranchise],
  (selectedFranchise): FranchiseDeliveryConfig[] =>
    selectedFranchise?.franchiseConfig?.deliveryV2 ?? NO_DELIVERY_CONFIGS
);

// Optional-chained on purpose. The real store always has a delivery slice, but
// these are read from components whose tests build partial states, and a
// selector that throws on a missing slice makes every one of those fixtures
// declare a slice it does not care about.
const selectCurrentDeliveryConfigId = (state: RootState): number | null =>
  state.delivery?.currentDeliveryConfigId ?? null;

const selectSelectedAddress = (state: RootState) =>
  state.delivery?.selectedAddress ?? null;

/**
 * The stored config exactly as the guest last chose it, matched on its own id
 * across every active branch -- and NOT scoped to the branch being ordered from.
 *
 * `.find` hands back the object that is already in the store, so the reference
 * is stable for as long as the franchise is -- which is what keeps every
 * component reading this from re-rendering on unrelated state changes.
 *
 * Only the map's address-confirm flow (`useSearchBox`) should read this. It
 * deliberately holds another branch's config while the old branch is still
 * selected -- the V3 tab commits the first config it finds for the active type
 * -- and it needs that config's own zones to tell which branch covers the
 * address it is about to confirm. Handing it the re-pointed row below made it
 * test an address in branch B against branch A's zone, report the address as
 * outside delivery, and never move the guest to B (prepits/project#3913 UX
 * case 1). Everything that quotes or places an order must use
 * `selectCurrentDeliveryConfig`.
 */
export const selectStoredDeliveryConfig: (
  state: RootState
) => FranchiseDeliveryConfig | null = createSelector(
  [selectDeliveryV2, selectCurrentDeliveryConfigId],
  (deliveryV2, id) => {
    // Checked against null rather than for truthiness: id 0 is a legitimate
    // row id, and a truthiness test would make that one config permanently
    // unresolvable while every other id worked.
    if (id === null || id === undefined) {
      return null;
    }

    return deliveryV2?.find((config) => config.id === id) ?? null;
  }
);

/**
 * The stored config, scoped to the branch being ordered from.
 *
 * The stored id can belong to a branch the guest is no longer on. It is
 * persisted to localStorage, so it outlives the session, and the id lookup
 * above spans every active branch -- so a guest who last ordered delivery from
 * branch A and comes back to branch B resolves A's row, which is a real config
 * and shows no error anywhere in the UI. Checkout then sent A's
 * `franchiseDeliveryConfigId` alongside B's `franchiseSlug`, and core -- which
 * only ever looks at the ordering branch's own `franchiseConfig.deliveryV2` --
 * answered 452 `franchiseDeliveryConfigNotFound`. Retrying could not clear it:
 * nothing in the flow rewrites the stored id, so the guest was locked out of
 * checkout until they happened to re-pick a branch (PD Q2L0JN4NY2ZBF4).
 *
 * So a foreign row is re-pointed onto the selected branch's config for the same
 * delivery type. Doing it here rather than at submit is deliberate: fee, ETA and
 * zone all derive from this selector, so the cart quotes the same branch's
 * config that the order is placed against. Re-pointing at submit alone would
 * have turned a hard failure into a quote that disagrees with the charge.
 *
 * The branch may genuinely not offer that delivery type, in which case the
 * foreign row stands and core rejects the order as it does today. Swapping in a
 * different delivery type would be worse than failing: the guest picked car
 * pickup, not delivery.
 */
export const selectCurrentDeliveryConfig: (
  state: RootState
) => FranchiseDeliveryConfig | null = createSelector(
  [selectStoredDeliveryConfig, selectSelectedFranchiseDeliveryV2],
  (matched, selectedFranchiseDeliveryV2) => {
    if (!matched) {
      return null;
    }

    // Already the selected branch's own row -- the common case, and the only
    // one where the id itself is what checkout should send.
    if (
      selectedFranchiseDeliveryV2.some((config) => config.id === matched.id)
    ) {
      return matched;
    }

    // Foreign row. Keep the guest's choice of delivery TYPE and re-point it at
    // the branch they are actually ordering from.
    const matchedDeliveryTypeId = matched.deliveryType?.id;

    // No delivery type to match on: re-pointing would be a guess, and matching
    // undefined against undefined would pick whichever untyped config came
    // first. Leave it as it is and let core have the final say.
    if (matchedDeliveryTypeId === null || matchedDeliveryTypeId === undefined) {
      return matched;
    }

    return (
      selectedFranchiseDeliveryV2.find(
        (config) => config.deliveryType?.id === matchedDeliveryTypeId
      ) ?? matched
    );
  }
);

export const selectDeliveryType: (state: RootState) => DeliveryType | null =
  createSelector(
    [selectCurrentDeliveryConfig],
    (config) => config?.deliveryType ?? null
  );

/**
 * The zone the guest's address falls in, against the CURRENT polygons.
 *
 * Null for a flat-fee config -- those carry their fee on the config row and
 * have no zones to match -- and null with no address, which is the state before
 * the guest has committed one. The map screens resolve a zone for a pin that is
 * not an address yet from their own local map centre; that is a live preview
 * during an interaction, not something the store needs to remember.
 */
const selectDeliveryZone = createSelector(
  [selectCurrentDeliveryConfig, selectSelectedAddress],
  (config, address) => {
    if (!config?.usesMap || !address) {
      return null;
    }

    if (
      !Number.isFinite(Number(address.lat)) ||
      !Number.isFinite(Number(address.lng))
    ) {
      return null;
    }

    return inFranchiseDeliveryZoneV2(
      { lat: Number(address.lat), lng: Number(address.lng) },
      config.deliveryConfigZones ?? []
    );
  }
);

/**
 * Zone fee for a map config, flat fee for one without zones, null when neither
 * resolves -- the same three cases the reducer used to write into the slice.
 */
export const selectDeliveryFees: (state: RootState) => number | null =
  createSelector(
    [selectCurrentDeliveryConfig, selectDeliveryZone],
    (config, zone) => {
      if (!config) {
        return null;
      }

      return config.usesMap ? zone?.deliveryFees ?? null : config.deliveryFees;
    }
  );

/**
 * The delivery context the loyalty calculate sends beside the fee, so the
 * backend prices delivery from the zone the charge will use.
 *
 * Only a DELIVERYV2 order is charged a delivery fee at checkout, and the saved
 * delivery config outlives a switch to pickup or dine-in, so any other order
 * type sends none. The address only matters to a map config. Not memoised:
 * callers compare the two ids, never the object.
 */
export const selectQuoteDeliveryContext = (
  state: RootState
): { franchiseDeliveryConfigId?: number; customerAddressId?: number } => {
  const config =
    state.queryParams?.orderTypeParam === OrderTypesEnum.DELIVERYV2
      ? selectCurrentDeliveryConfig(state)
      : null;

  return {
    franchiseDeliveryConfigId: config?.id,
    customerAddressId: config?.usesMap
      ? state.delivery?.selectedAddress?.id
      : undefined,
  };
};

/**
 * 30 and 45 are the defaults the slice carried, and several screens read these
 * before any delivery selection exists at all (an item label's prep estimate,
 * the bottom sheet), so they must stay numbers rather than going null.
 */
const DEFAULT_MIN_ESTIMATED_TIME = 30;
const DEFAULT_MAX_ESTIMATED_TIME = 45;

export const selectMinEstimatedTime: (state: RootState) => number =
  createSelector(
    [selectCurrentDeliveryConfig, selectDeliveryZone],
    (config, zone) => {
      if (!config) {
        return DEFAULT_MIN_ESTIMATED_TIME;
      }

      return config.usesMap
        ? zone?.minEstimatedTime ?? DEFAULT_MIN_ESTIMATED_TIME
        : config.minEstimatedTime;
    }
  );

export const selectMaxEstimatedTime: (state: RootState) => number =
  createSelector(
    [selectCurrentDeliveryConfig, selectDeliveryZone],
    (config, zone) => {
      if (!config) {
        return DEFAULT_MAX_ESTIMATED_TIME;
      }

      return config.usesMap
        ? zone?.maxEstimatedTime ?? DEFAULT_MAX_ESTIMATED_TIME
        : config.maxEstimatedTime;
    }
  );

export const selectDeliveryZoneId: (state: RootState) => number | null =
  createSelector([selectDeliveryZone], (zone) => zone?.deliveryZoneId ?? null);
